import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Users, 
  Calendar, 
  MapPin, 
  Mail, 
  Phone, 
  Edit, 
  Plus, 
  X, 
  CheckCircle, 
  Clock, 
  ExternalLink,
  Trophy,
  AlertTriangle,
  UserCheck,
  UserX,
  Copy,
  RefreshCw,
  FileText,
  Download,
  Upload,
  Eye,
  UserPlus,
  LogOut,
  AlertCircle,
  Trash2,
  XCircle,
  User
} from 'lucide-react';
import { teamAPI } from '../../services/api';
import { uploadAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import SubmissionFileUpload from '../../components/ui/submission-file-upload';
import ScoringResultsDisplay from '../../components/ui/ScoringResultsDisplay';

interface Team {
  _id: string;
  name: string;
  hackathon: {
    _id: string;
    title: string;
    startDate: string;
    endDate: string;
    status: string;
    maxTeamSize: number;
    minTeamSize: number;
    phases?: Array<{
      _id: string;
      name: string;
      description: string;
      startDate: string;
      endDate: string;
      status: string;
      order: number;
    }>;
  };
  leader: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  members: Array<{
    user: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      address?: string;
      skills?: {
        technical?: string[];
        soft?: string[];
      };
      primarySkills?: string[];
      secondarySkills?: string[];
    };
    role: string;
    joinedAt: string;
  }>;
  status: string;
  isApproved: boolean;
  isActive: boolean;
  joinCode: string;
  joinCodeExpiresAt?: string;
  rejectionReason?: string;
  selectedProblemStatement?: {
    title: string;
    description: string;
    category: string;
    difficulty: string;
  };
  assignedProblemStatement?: {
    title: string;
    description: string;
    category: string;
    difficulty: string;
  };
  assignedMentor?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    address?: string;
    skills?: {
      technical?: string[];
      soft?: string[];
    };
    primarySkills?: string[];
    secondarySkills?: string[];
    experience?: {
      yearsOfExperience?: number;
    };
    education?: {
      graduationYear?: number;
    };
    professionalInfo?: {
      yearsOfExperience?: number;
    };
  };
  submission?: {
    title: string;
    description: string;
    githubLink: string;
    demoLink: string;
    presentationLink: string;
    submittedAt: string;
    // Enhanced submission fields
    presentation?: {
      file: string;
      remarks: string;
      uploadedAt: string;
    };
    githubRepository?: {
      link: string;
      remarks: string;
      submittedAt: string;
    };
    additionalDocuments?: Array<{
      name: string;
      file: string;
      remarks: string;
      uploadedAt: string;
    }>;
    generalRemarks: string;
  };
  submissionScore?: {
    overallScore?: number;
    phaseScores?: any;
    juryMember?: {
      name: string;
      userId: string;
    };
    generalFeedback?: string;
    scoredAt: string;
    // Legacy support
    score?: number;
    feedback?: string;
    scoredBy?: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  createdAt: string;
  teamNotes?: string;
  isLookingForMembers?: boolean;
  pendingApplications?: Array<{
    user: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      address?: string;
    };
    notes?: string;
    appliedAt: string;
    status?: string;
  }>;
  // Admin control for submission editing
  allowResubmission?: boolean;
}

const TeamDetail: React.FC = () => {
  console.log('TeamDetail component rendering...');
  
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { user } = useAuth();
  
  console.log('TeamDetail - id:', id, 'user:', user);
  
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [teamNotes, setTeamNotes] = useState('');
  const [isLookingForMembers, setIsLookingForMembers] = useState(false);
  const [updatingNotes, setUpdatingNotes] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingApplication, setProcessingApplication] = useState<string | null>(null);
  const [showRemoveMemberDialog, setShowRemoveMemberDialog] = useState(false);
  const [showLeaveTeamDialog, setShowLeaveTeamDialog] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
  const [isEditingSubmission, setIsEditingSubmission] = useState(false);
  const [submissionData, setSubmissionData] = useState({
    title: '',
    description: '',
    githubLink: '',
    demoLink: '',
    presentationLink: '',
    generalRemarks: '',
    presentation: {
      file: '',
      remarks: ''
    },
    githubRepository: {
      link: '',
      remarks: ''
    },
    additionalDocuments: [] as Array<{
      name: string;
      file: string;
      remarks: string;
    }>
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [regeneratingCode, setRegeneratingCode] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);


  useEffect(() => {
    if (id) {
      fetchTeam();
    }
  }, [id]);

  useEffect(() => {
    if (team) {
      setTeamNotes(team.teamNotes || '');
      setIsLookingForMembers(team.isLookingForMembers || false);
    }
  }, [team]);

  // Set up automatic countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Auto-generate new code if expired when team data loads
  useEffect(() => {
    if (team && isJoinCodeExpired() && isTeamLeader()) {
      autoGenerateCodeIfExpired();
    }
  }, [team?.joinCodeExpiresAt]); // Only run when joinCodeExpiresAt changes

  // Auto-generate new code when current code expires while user is on the page
  useEffect(() => {
    if (team && team.joinCodeExpiresAt && isTeamLeader()) {
      const expiry = new Date(team.joinCodeExpiresAt);
      const diff = expiry.getTime() - currentTime.getTime();
      
      // If code just expired (within last second), generate new one
      if (diff <= 0 && diff > -1000) {
        console.log('🔄 Join code just expired, auto-generating new one');
        autoGenerateCodeIfExpired();
      }
    }
  }, [currentTime, team?.joinCodeExpiresAt]); // Check every second and when expiry changes

  const fetchTeam = async () => {
    try {
      console.log('Fetching team with ID:', id);
      setLoading(true);
      const response = await teamAPI.getTeamById(id!);
      console.log('Team fetched successfully:', response);
      setTeam(response);
    } catch (err: any) {
      console.error('Error fetching team:', err);
      error(err.response?.data?.message || 'Failed to fetch team');
      navigate('/teams/my-teams');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitProject = async () => {
    try {
      await teamAPI.submitProject(id!, submissionData);
      success(isEditingSubmission ? 'Project submission updated successfully!' : 'Project submitted successfully!');
      setShowSubmissionDialog(false);
      setIsEditingSubmission(false);
      fetchTeam(); // Refresh team data
    } catch (err: any) {
      error(err.response?.data?.message || (isEditingSubmission ? 'Failed to update project submission' : 'Failed to submit project'));
    }
  };

  const openSubmissionDialog = (editing = false) => {
    setIsEditingSubmission(editing);
    
    // Get title and description from assigned problem statement
    const problemStatement = team?.assignedProblemStatement || team?.selectedProblemStatement;
    const projectTitle = problemStatement?.title || '';
    const projectDescription = problemStatement?.description || '';
    
    if (editing && team?.submission) {
      // Pre-populate with existing submission data, but use problem statement for title/description
      setSubmissionData({
        title: projectTitle,
        description: projectDescription,
        githubLink: team.submission.githubLink || '',
        demoLink: team.submission.demoLink || '',
        presentationLink: team.submission.presentationLink || '',
        generalRemarks: team.submission.generalRemarks || '',
        presentation: {
          file: team.submission.presentation?.file || '',
          remarks: team.submission.presentation?.remarks || ''
        },
        githubRepository: {
          link: team.submission.githubRepository?.link || team.submission.githubLink || '',
          remarks: team.submission.githubRepository?.remarks || ''
        },
        additionalDocuments: team.submission.additionalDocuments?.map(doc => ({
          name: doc.name,
          file: doc.file,
          remarks: doc.remarks
        })) || []
      });
    } else {
      // Reset to empty state for new submission, but populate from problem statement
      setSubmissionData({
        title: projectTitle,
        description: projectDescription,
        githubLink: '',
        demoLink: '',
        presentationLink: '',
        generalRemarks: '',
        presentation: {
          file: '',
          remarks: ''
        },
        githubRepository: {
          link: '',
          remarks: ''
        },
        additionalDocuments: []
      });
    }
    
    setShowSubmissionDialog(true);
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    
    try {
      const response = await teamAPI.removeMember(id!, memberToRemove);
      
      // Check if team was dissolved
      if (response.dissolved) {
        success('Member removed. Team has been dissolved because the leader was the last remaining member.');
        navigate('/teams/my-teams'); // Redirect to teams list
        return;
      }
      
      success('Member removed successfully');
      setShowRemoveMemberDialog(false);
      setMemberToRemove(null);
      fetchTeam(); // Refresh team data
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleLeaveTeam = async () => {
    setShowLeaveTeamDialog(true);
  };

  const confirmLeaveTeam = async () => {
    try {
      const response = await teamAPI.leaveTeam(id!);
      
      // Check if team was dissolved
      if (response.dissolved) {
        success('You have left the team. The team has been dissolved because you were the last remaining member.');
        navigate('/teams/my-teams');
        return;
      }
      
      success('You have successfully left the team.');
      navigate('/teams/my-teams');
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to leave team');
    } finally {
      setShowLeaveTeamDialog(false);
    }
  };

  const handleUpdateTeamNotes = async () => {
    try {
      setUpdatingNotes(true);
      await teamAPI.updateTeamNotes(id!, teamNotes, isLookingForMembers);
      success('Team notes updated successfully');
      setShowNotesDialog(false);
      fetchTeam(); // Refresh team data
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to update team notes');
    } finally {
      setUpdatingNotes(false);
    }
  };

  const handleToggleLookingForMembers = async () => {
    if (!team) return;
    
    try {
      const response = await teamAPI.updateTeamNotes(team._id, team.teamNotes, !team.isLookingForMembers);
      setTeam(response.team);
      success(`Team is now ${!team.isLookingForMembers ? 'looking for' : 'not looking for'} members`);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to update team status');
    }
  };

  const handleAcceptApplication = async (userId: string) => {
    try {
      setProcessingApplication(userId);
      await teamAPI.acceptApplication(id!, userId);
      success('Application accepted successfully');
      fetchTeam(); // Refresh team data
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to accept application');
    } finally {
      setProcessingApplication(null);
    }
  };

  const handleRejectApplication = async () => {
    if (!selectedApplication) return;
    
    try {
      setProcessingApplication(selectedApplication.user._id);
      await teamAPI.rejectApplication(id!, selectedApplication.user._id, rejectionReason);
      success('Application rejected successfully');
      setShowRejectDialog(false);
      setSelectedApplication(null);
      setRejectionReason('');
      fetchTeam(); // Refresh team data
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to reject application');
    } finally {
      setProcessingApplication(null);
    }
  };

  const [showToggleStatusDialog, setShowToggleStatusDialog] = useState(false);

  const handleToggleTeamStatus = () => {
    setShowToggleStatusDialog(true);
  };

  const confirmToggleTeamStatus = async () => {
    try {
      if (team?.isActive) {
        const response = await teamAPI.deactivateTeam(id!);
        success('Team deactivated successfully');
      } else {
        const response = await teamAPI.activateTeam(id!);
        success('Team activated successfully');
      }
      fetchTeam(); // Refresh team data
    } catch (err: any) {
      error(err.response?.data?.message || `Failed to ${team?.isActive ? 'deactivate' : 'activate'} team`);
    } finally {
      setShowToggleStatusDialog(false);
    }
  };

  const copyJoinCode = () => {
    if (team) {
      navigator.clipboard.writeText(team.joinCode);
      success('Join code copied to clipboard!');
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000); // Hide after 2 seconds
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      console.log('🔄 Submitting team for approval:', { teamId: id, teamStatus: team?.status, memberCount: team?.members.length });
      
      // Show loading state
      success('Submitting team for approval...');
      
      const response = await teamAPI.submitForApproval(id!);
      console.log('✅ Team submission response:', response);
      
      // Show success message with more detail
      success('🎉 Team submitted for approval successfully! Your team is now under review.');
      fetchTeam(); // Refresh team data
    } catch (err: any) {
      console.error('❌ Team submission error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to submit team for approval';
      error(errorMessage);
      
      // Show more detailed error information
      if (err.response?.data?.errors) {
        console.error('Validation errors:', err.response.data.errors);
      }
    }
  };

  const handleRegenerateJoinCode = async () => {
    try {
      setRegeneratingCode(true);
      const response = await teamAPI.regenerateJoinCode(id!);
      success('Join code regenerated successfully!');
      fetchTeam(); // Refresh team data
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to regenerate join code');
    } finally {
      setRegeneratingCode(false);
    }
  };

  const handleToggleResubmission = async () => {
    if (!team) return;
    
    try {
      const response = await teamAPI.toggleResubmission(team._id);
      setTeam(response.data);
      success(team.allowResubmission ? 'Resubmission disabled' : 'Resubmission enabled');
    } catch (error: any) {
      error(error.response?.data?.message || 'Failed to toggle resubmission');
    }
  };

  // Auto-generate new code if expired when page loads
  const autoGenerateCodeIfExpired = async () => {
    if (team && isJoinCodeExpired() && isTeamLeader()) {
      console.log('🔄 Auto-generating new join code as current one is expired');
      try {
        await teamAPI.regenerateJoinCode(id!);
        fetchTeam(); // Refresh team data
      } catch (err: any) {
        console.error('❌ Failed to auto-generate join code:', err);
      }
    }
  };

  const isJoinCodeExpired = () => {
    if (!team?.joinCodeExpiresAt) return false;
    return currentTime > new Date(team.joinCodeExpiresAt);
  };

  const getTimeUntilExpiry = () => {
    if (!team?.joinCodeExpiresAt) return null;
    const expiry = new Date(team.joinCodeExpiresAt);
    const diff = expiry.getTime() - currentTime.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const minutes = Math.floor(diff / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${minutes}m ${seconds}s`;
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'forming': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const isTeamLeader = () => {
    return team?.leader._id === user?._id;
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const canSubmitProject = () => {
    if (!isTeamLeader() || (team?.status !== 'active' && team?.status !== 'approved' && team?.status !== 'completed')) {
      return false;
    }
    
    // Check if team has an assigned problem statement
    const hasProblemStatement = team?.assignedProblemStatement || team?.selectedProblemStatement;
    if (!hasProblemStatement) {
      return false;
    }
    
    // Check if submission deadline has passed
    const now = new Date();
    const hackathonEndDate = new Date(team.hackathon?.endDate || new Date());
    
    return now <= hackathonEndDate;
  };

  const canEditSubmission = () => {
    const canEdit = isTeamLeader() && 
                   (team?.status === 'active' || team?.status === 'approved' || team?.status === 'completed') && 
                   team?.submission && 
                   team?.allowResubmission;
    return canEdit;
  };

  const getSubmissionDeadlineStatus = () => {
    if (!team || !team.hackathon?.endDate) return null;
    
    const deadline = new Date(team.hackathon.endDate);
    const now = new Date();
    const diffTime = deadline.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'overdue', message: 'Deadline has passed' };
    } else if (diffDays <= 1) {
      return { status: 'urgent', message: 'Due today!' };
    } else if (diffDays <= 3) {
      return { status: 'warning', message: `Due in ${diffDays} days` };
    } else {
      return { status: 'normal', message: `Due in ${diffDays} days` };
    }
  };

  // Helper function to extract city from address
  const getCityFromAddress = (address: string) => {
    if (!address) return 'Location not specified';
    
    const addressParts = address.split(',').map((part: string) => part.trim());
    
    // Look for city (usually the 5th or 6th part from the end)
    for (let i = addressParts.length - 1; i >= 0; i--) {
      const part = addressParts[i];
      // Skip postal codes, state names, and country
      if (!/^\d{6}$/.test(part) && 
          !['India', 'Telangana', 'Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu', 'Kerala', 'Andhra Pradesh'].includes(part)) {
        return part;
      }
    }
    
    return addressParts[addressParts.length - 2] || 'Location not specified';
  };

  // Helper function to get user skills
  const getUserSkills = (user: any) => {
    const skills = [];
    
    // Add technical skills
    if (user.skills?.technical && Array.isArray(user.skills.technical)) {
      skills.push(...user.skills.technical);
    }
    
    // Add soft skills
    if (user.skills?.soft && Array.isArray(user.skills.soft)) {
      skills.push(...user.skills.soft);
    }
    
    // Add primary skills
    if (user.primarySkills && Array.isArray(user.primarySkills)) {
      skills.push(...user.primarySkills);
    }
    
    // Add secondary skills
    if (user.secondarySkills && Array.isArray(user.secondarySkills)) {
      skills.push(...user.secondarySkills);
    }
    
    // Remove duplicates and return unique skills
    return [...new Set(skills)].slice(0, 5); // Limit to 5 skills
  };

  // Helper function to get user experience
  const getUserExperience = (user: any) => {
    if (user.professionalInfo?.yearsOfExperience) {
      return `${user.professionalInfo.yearsOfExperience} years`;
    }
    if (user.education?.graduationYear) {
      const currentYear = new Date().getFullYear();
      const yearsSinceGraduation = currentYear - user.education.graduationYear;
      if (yearsSinceGraduation >= 0) {
        return yearsSinceGraduation === 0 ? '' : `${yearsSinceGraduation} years`;
      }
    }
    return '';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">Team not found</div>
      </div>
    );
  }

  try {
    console.log('Rendering team detail with team:', team);
    
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <Link to="/teams/my-teams" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">
          ← Back to My Teams
        </Link>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{team.name}</h1>
            <div className="flex items-center gap-4">
              <Badge className={getStatusColor(team.status)}>
                {team.status.replace('_', ' ').toUpperCase()}
              </Badge>
              {/* Only show isActive badge if it provides different information than status */}
              {(team.status !== 'active' || !team.isActive) && (
                <Badge className={team.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                  {team.isActive ? 'ACTIVE' : 'INACTIVE'}
                </Badge>
              )}
              {team.submission && (
                <Badge 
                  className={team.allowResubmission 
                    ? "bg-yellow-100 text-yellow-800 border-yellow-300" 
                    : "bg-green-100 text-green-800 border-green-300"
                  }
                >
                  {team.allowResubmission ? "PROJECT NOT SUBMITTED" : "PROJECT SUBMITTED"}
                </Badge>
              )}
              {/* Role indicator */}
              {isTeamLeader() ? (
                <span className="text-sm text-green-700 font-medium">
                  You are the leader
                </span>
              ) : (
                <span className="text-sm text-blue-700 font-medium">
                  You are a Member
                </span>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            <Link to={`/hackathons/${team.hackathon._id}`}>
              <Button variant="outline">
                <Eye className="h-4 w-4 mr-2" />
                View Hackathon
              </Button>
            </Link>
            
            {(isTeamLeader() || user?.role === 'admin') && team.members.length < team.hackathon.maxTeamSize && (
              <Link to={`/teams/${team._id}/add-members`}>
                <Button variant="outline" className="text-blue-600 hover:text-blue-700 border-blue-300 hover:border-blue-400">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Member
                </Button>
              </Link>
            )}
            
            {!isTeamLeader() && (
              <Button 
                onClick={handleLeaveTeam}
                variant="outline"
                className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Leave Team
              </Button>
            )}
            
            {(isTeamLeader() || user?.role === 'admin') && (
              <Button 
                onClick={handleToggleTeamStatus}
                variant="outline"
                className={team.isActive 
                  ? "text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                  : "text-green-600 hover:text-green-700 border-green-300 hover:border-green-400"
                }
              >
                {team.isActive ? 'Deactivate Team' : 'Activate Team'}
              </Button>
            )}

            
            {isTeamLeader() && team.status === 'forming' && team.members.length >= team.hackathon.minTeamSize && (
              <Button 
                onClick={() => handleSubmitForApproval()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Submit for Approval
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Hackathon Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Hackathon Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h3 className="text-xl font-semibold mb-2">{team.hackathon?.title || 'No hackathon assigned'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Start Date</p>
                  <p className="font-medium">{formatDate(team.hackathon?.startDate || new Date().toISOString())} at {formatTime(team.hackathon?.startDate || new Date().toISOString())}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">End Date</p>
                  <p className="font-medium">{formatDate(team.hackathon.endDate)} at {formatTime(team.hackathon.endDate)}</p>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Status</div>
                  <Badge className={getStatusColor(team.hackathon.status)}>
                    {team.hackathon.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Team Size</p>
                  <p className="font-medium">{team.members.length}/{team.hackathon.maxTeamSize}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Problem Statement & Submissions Tabs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <FileText className="h-6 w-6 text-green-500" />
                Project Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="problem-statement" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="problem-statement">Problem Statement</TabsTrigger>
                  <TabsTrigger value="submissions">Submissions</TabsTrigger>
                  <TabsTrigger value="general-feedback">General Feedback</TabsTrigger>
                </TabsList>
                
                <TabsContent value="problem-statement" className="mt-6">
                  {team.assignedProblemStatement ? (
                    <div>
                      <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg shadow-sm">
                        <h5 className="font-bold text-green-800 mb-3 text-lg">✓ Assigned Problem Statement</h5>
                        <div className="mb-4">
                          <h4 className="text-xl font-bold text-green-900 mb-3 border-b border-green-200 pb-2">{team.assignedProblemStatement.title}</h4>
                          <div className="bg-white p-3 rounded-lg border border-green-100">
                            <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{team.assignedProblemStatement.description}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {team.assignedProblemStatement.category && (
                            <Badge variant="outline">{team.assignedProblemStatement.category}</Badge>
                          )}
                          {team.assignedProblemStatement.difficulty && (
                            <Badge variant="outline">{team.assignedProblemStatement.difficulty}</Badge>
                          )}
                        </div>
                      </div>
                      {team.selectedProblemStatement && (
                        <div className="mt-4 p-4 bg-gradient-to-r from-gray-50 to-slate-50 border-2 border-gray-200 rounded-lg shadow-sm">
                          <h5 className="font-bold text-gray-800 mb-3 text-lg">⏳ Originally Selected</h5>
                          <div className="mb-4">
                            <h4 className="text-xl font-bold text-gray-900 mb-3 border-b border-gray-200 pb-2">{team.selectedProblemStatement.title}</h4>
                            <div className="bg-white p-3 rounded-lg border border-gray-100">
                              <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{team.selectedProblemStatement.description}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {team.selectedProblemStatement.category && (
                              <Badge variant="outline">{team.selectedProblemStatement.category}</Badge>
                            )}
                            {team.selectedProblemStatement.difficulty && (
                              <Badge variant="outline">{team.selectedProblemStatement.difficulty}</Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : team.selectedProblemStatement ? (
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg shadow-sm">
                      <h5 className="font-bold text-blue-800 mb-3 text-lg">⏳ Selected Problem Statement (Pending Assignment)</h5>
                      <div className="mb-4">
                        <h4 className="text-xl font-bold text-blue-900 mb-3 border-b border-blue-200 pb-2">{team.selectedProblemStatement.title}</h4>
                        <div className="bg-white p-3 rounded-lg border border-blue-100">
                          <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{team.selectedProblemStatement.description}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {team.selectedProblemStatement.category && (
                          <Badge variant="outline">{team.selectedProblemStatement.category}</Badge>
                        )}
                        {team.selectedProblemStatement.difficulty && (
                          <Badge variant="outline">{team.selectedProblemStatement.difficulty}</Badge>
                        )}
                      </div>
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-yellow-800 text-sm">
                          Waiting for admin to assign final problem statement
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-600">No problem statement assigned yet</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="submissions" className="mt-6">
                  {team.submission ? (
                    <div className="space-y-6">
                      {/* Project Information */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-3">Project Information</h4>
                        <div className="space-y-2">
                          <div>
                            <strong>Title:</strong> {team.submission.title || team.assignedProblemStatement?.title || team.selectedProblemStatement?.title || 'No title provided'}
                          </div>
                          <div>
                            <strong>Description:</strong>
                            <p className="text-gray-700 mt-1">{team.submission.description || team.assignedProblemStatement?.description || team.selectedProblemStatement?.description || 'No description provided'}</p>
                          </div>
                          {team.submission.generalRemarks && (
                            <div>
                              <strong>General Remarks:</strong>
                              <p className="text-gray-700 mt-1">{team.submission.generalRemarks}</p>
                            </div>
                          )}
                          <div>
                            <strong>Submitted:</strong> {formatDate(team.submission.submittedAt)}
                          </div>
                        </div>
                      </div>

                      {/* Presentation */}
                      {(team.submission.presentation?.file || team.submission.presentationLink) && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <h4 className="font-semibold text-green-800 mb-3">Presentation</h4>
                          <div className="space-y-2">
                            {team.submission.presentation?.file && (
                              <div>
                                <strong>File:</strong> 
                                <div className="flex items-center gap-2 mt-1">
                                  <FileText className="h-4 w-4 text-green-600" />
                                  <span className="text-green-700">{team.submission.presentation.file.split('/').pop() || 'Presentation file uploaded'}</span>
                                </div>
                                {team.submission.presentation.remarks && (
                                  <div className="mt-2">
                                    <strong>Remarks:</strong>
                                    <p className="text-gray-700 mt-1">{team.submission.presentation.remarks}</p>
                                  </div>
                                )}
                              </div>
                            )}
                            {team.submission.presentationLink && (
                              <div>
                                <strong>Link:</strong>
                                <a 
                                  href={team.submission.presentationLink} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 ml-2"
                                >
                                  View Presentation
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* GitHub Repository */}
                      {(team.submission.githubRepository?.link || team.submission.githubLink) && (
                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                          <h4 className="font-semibold text-purple-800 mb-3">GitHub Repository</h4>
                          <div className="space-y-2">
                            <div>
                              <strong>Repository:</strong>
                              <a 
                                href={team.submission.githubRepository?.link || team.submission.githubLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 ml-2"
                              >
                                View Repository
                              </a>
                            </div>
                            {team.submission.githubRepository?.remarks && (
                              <div>
                                <strong>Remarks:</strong>
                                <p className="text-gray-700 mt-1">{team.submission.githubRepository.remarks}</p>
                              </div>
                            )}
                            {team.submission.demoLink && (
                              <div>
                                <strong>Demo Link:</strong>
                                <a 
                                  href={team.submission.demoLink} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 ml-2"
                                >
                                  View Demo
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Additional Documents */}
                      {team.submission.additionalDocuments && team.submission.additionalDocuments.length > 0 && (
                        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                          <h4 className="font-semibold text-orange-800 mb-3">Additional Documents</h4>
                          <div className="space-y-3">
                            {team.submission.additionalDocuments.map((doc, index) => (
                              <div key={index} className="border border-orange-200 rounded p-3 bg-white">
                                <div className="flex items-center gap-2 mb-2">
                                  <FileText className="h-4 w-4 text-orange-600" />
                                  <strong>{doc.name}</strong>
                                </div>
                                {doc.remarks && (
                                  <div className="mb-2">
                                    <strong>Remarks:</strong>
                                    <p className="text-gray-700 mt-1">{doc.remarks}</p>
                                  </div>
                                )}
                                <div className="text-sm text-gray-600">
                                  Uploaded: {formatDate(doc.uploadedAt)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Submission Score - Only show for admins */}
                      {isAdmin() && team.submissionScore && (
                        <div className="mb-6">
                          <h4 className="font-semibold text-yellow-800 mb-3">Submission Score</h4>
                          <ScoringResultsDisplay 
                            submissionScore={team.submissionScore} 
                            showDetailedScores={true} // Always show detailed scores for admins
                          />
                        </div>
                      )}

                      {/* Admin Control for Resubmission */}
                      {isAdmin() && team.submission && (
                        <div className="flex justify-center pt-4">
                          <Button 
                            onClick={handleToggleResubmission}
                            variant={team.allowResubmission ? "destructive" : "default"}
                          >
                            {team.allowResubmission ? 'Disable Resubmission' : 'Allow Resubmission'}
                          </Button>
                        </div>
                      )}

                      {/* Edit Submission Button - Only shown if admin allows resubmission */}
                      {canEditSubmission() && (
                        <div className="flex justify-center pt-4">
                          <Button onClick={() => openSubmissionDialog(true)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Submit / Edit & Resubmit
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-600 mb-4">No submission available yet</p>
                      {canSubmitProject() && (
                        <Button onClick={() => openSubmissionDialog(false)}>
                          <Upload className="h-4 w-4 mr-2" />
                          Submit Project
                        </Button>
                      )}
                      {!canSubmitProject() && isTeamLeader() && (
                        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-yellow-800 text-sm">
                            {!team?.assignedProblemStatement && !team?.selectedProblemStatement 
                              ? 'You need an assigned problem statement before you can submit your project.'
                              : 'Submission is not available at this time.'
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="general-feedback" className="mt-6">
                  {team.submissionScore ? (
                    <div className="space-y-6">
                      {/* Jury Information */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <User className="h-5 w-5 text-blue-600" />
                            Jury Information
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <strong>Jury Member:</strong>
                              <p className="text-gray-700">{team.submissionScore.juryMember?.name || 'N/A'}</p>
                            </div>
                            <div>
                              <strong>Scored At:</strong>
                              <p className="text-gray-700">{team.submissionScore.scoredAt ? new Date(team.submissionScore.scoredAt).toLocaleString() : 'N/A'}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* General Feedback Only */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-green-600" />
                            Jury General Feedback
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {/* Show only general feedback */}
                            {team.submissionScore.generalFeedback && (
                              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                                <p className="text-gray-700 whitespace-pre-wrap">{team.submissionScore.generalFeedback}</p>
                              </div>
                            )}
                            
                            {/* Show simple feedback if no general feedback */}
                            {team.submissionScore.feedback && !team.submissionScore.generalFeedback && (
                              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                                <p className="text-gray-700">{team.submissionScore.feedback}</p>
                              </div>
                            )}
                            
                            {/* Show message if no feedback available */}
                            {!team.submissionScore.generalFeedback && 
                             !team.submissionScore.feedback && (
                              <div className="text-center py-8">
                                <p className="text-gray-500">No feedback available yet. The jury will provide comments after reviewing your submission.</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No scoring data available yet. The jury will provide feedback after reviewing your submission.</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Team Status & Mentor */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-blue-500" />
                Team Status & Mentor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">Team Status</h4>
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className={getStatusColor(team.status)}>
                      {team.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    {/* Project Status Badge */}
                    {team.submission && team.submission.submittedAt && (
                      <Badge 
                        className="bg-green-100 text-green-800 border-green-300"
                      >
                        Project Submitted
                      </Badge>
                    )}
                    {(!team.submission || !team.submission.submittedAt) && (
                      <Badge 
                        className="bg-yellow-100 text-yellow-800 border-yellow-300"
                      >
                        Project Not Submitted
                      </Badge>
                    )}
                    {/* Role indicator */}
                    {isTeamLeader() ? (
                      <span className="text-sm text-green-700 font-medium">
                        You are the leader
                      </span>
                    ) : (
                      <span className="text-sm text-blue-700 font-medium">
                        You are a Member
                      </span>
                    )}
                  </div>
                  {team.status === 'rejected' && team.rejectionReason && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-800 text-sm">
                        <strong>Reason:</strong> {team.rejectionReason}
                      </p>
                    </div>
                  )}
                  
                  {/* Team Member Count Messages */}
                  <div className="mt-3 space-y-1">
                    {team.members.length < team.hackathon.minTeamSize && (
                      <p className="text-sm text-orange-600">
                        Team needs at least {team.hackathon.minTeamSize} member(s) to submit for approval. Current members: {team.members.length}
                      </p>
                    )}
                    {team.members.length < team.hackathon.maxTeamSize && (
                      <p className="text-sm text-blue-600">
                        Team has {team.hackathon.maxTeamSize - team.members.length} spot(s) available
                      </p>
                    )}
                    {/* Submission Date */}
                    {team.submission && team.submission.submittedAt && (
                      <p className="text-sm text-green-600">
                        Project submitted on {formatDate(team.submission.submittedAt)} at {formatTime(team.submission.submittedAt)}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Assigned Mentor</h4>
                  {team.assignedMentor ? (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="font-medium mb-2">
                        {team.assignedMentor.firstName} {team.assignedMentor.lastName}
                      </div>
                      
                      {/* Contact Information */}
                      <div className="space-y-1 text-sm mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">📧</span>
                          <span>{team.assignedMentor.email}</span>
                        </div>
                        {team.assignedMentor.phone && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">📞</span>
                            <span>{team.assignedMentor.phone}</span>
                          </div>
                        )}
                        {team.assignedMentor.address && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">📍</span>
                            <span>{getCityFromAddress(team.assignedMentor.address)}</span>
                          </div>
                        )}
                      </div>

                      {/* Skills */}
                      {getUserSkills(team.assignedMentor).length > 0 && (
                        <div className="mb-3">
                          <h6 className="font-medium text-sm text-gray-700 mb-2">Skills:</h6>
                          <div className="flex flex-wrap gap-1">
                            {getUserSkills(team.assignedMentor).map((skill, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Experience */}
                      {(() => {
                        const experience = getUserExperience(team.assignedMentor);
                        return experience ? (
                          <div className="mb-3">
                            <h6 className="font-medium text-sm text-gray-700 mb-1">Experience:</h6>
                            <span className="text-sm text-gray-600">{experience}</span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                      <p className="text-gray-600 text-sm">No mentor assigned yet</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Team Members */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Members ({team.members.length}/{team.hackathon.maxTeamSize})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {team.members.map((member) => (
                  <div key={member.user._id} className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="font-medium text-lg">
                          {member.user.firstName} {member.user.lastName}
                          {member.role === 'leader' && (
                            <Badge className="ml-2 bg-blue-100 text-blue-800">Leader</Badge>
                          )}
                        </div>
                      </div>
                      {isTeamLeader() && member.role !== 'leader' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setMemberToRemove(member.user._id);
                            setShowRemoveMemberDialog(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Contact Information */}
                    <div className="space-y-1 text-sm mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">📧</span>
                        <span>{member.user.email}</span>
                      </div>
                      {member.user.phone && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">📞</span>
                          <span>{member.user.phone}</span>
                        </div>
                      )}
                      {member.user.address && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">📍</span>
                          <span>{getCityFromAddress(member.user.address)}</span>
                        </div>
                      )}
                    </div>

                    {/* Skills */}
                    {getUserSkills(member.user).length > 0 && (
                      <div className="mb-3">
                        <h6 className="font-medium text-sm text-gray-700 mb-2">Skills:</h6>
                        <div className="flex flex-wrap gap-1">
                          {getUserSkills(member.user).map((skill, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Join Date */}
                    <div className="text-xs text-gray-500">
                      Joined {formatDate(member.joinedAt)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Submission Deadline Status */}
          {team.status === 'active' && !team.submission && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-500" />
                  Submission Deadline
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const deadlineStatus = getSubmissionDeadlineStatus();
                  if (!deadlineStatus) return null;
                  
                  return (
                    <div className={`p-3 rounded-md ${
                      deadlineStatus.status === 'overdue' 
                        ? 'bg-red-50 border border-red-200' 
                        : deadlineStatus.status === 'urgent'
                        ? 'bg-red-50 border border-red-200'
                        : deadlineStatus.status === 'warning'
                        ? 'bg-yellow-50 border border-yellow-200'
                        : 'bg-blue-50 border border-blue-200'
                    }`}>
                      <p className={`text-sm font-medium ${
                        deadlineStatus.status === 'overdue' ? 'text-red-800' : deadlineStatus.status === 'urgent' ? 'text-red-800' : deadlineStatus.status === 'warning' ? 'text-yellow-800' : 'text-blue-800'
                      }`}>
                        {deadlineStatus.message}
                      </p>
                      {deadlineStatus.status === 'overdue' && (
                        <p className="text-red-700 text-xs mt-1">
                          Submissions are no longer accepted after the deadline.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}


        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Team Notes & Applications - Moved to top */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-purple-500" />
                Team Notes & Applications
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Team Notes */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">Team Notes</h4>
                  {isTeamLeader() && (
                    <Button
                      onClick={() => setShowNotesDialog(true)}
                      variant="outline"
                      size="sm"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
                {team.teamNotes ? (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-gray-800">{team.teamNotes}</p>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-gray-500 italic">No team notes added yet</p>
                  </div>
                )}
                
                {/* Looking for Members Status */}
                <div className="mt-3 flex items-center gap-2">
                  <Badge className={team.isLookingForMembers ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"}>
                    {team.isLookingForMembers ? "Looking for members" : "Not looking for members"}
                  </Badge>
                  {isTeamLeader() && (
                    <Button
                      onClick={handleToggleLookingForMembers}
                      variant="outline"
                      size="sm"
                      className={team.isLookingForMembers 
                        ? "text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                        : "text-blue-600 hover:text-blue-700 border-blue-300 hover:border-blue-400"
                      }
                    >
                      {team.isLookingForMembers ? (
                        <>
                          <XCircle className="h-4 w-4 mr-1" />
                          Stop Looking
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-1" />
                          Start Looking
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Pending Applications */}
              {isTeamLeader() && team.pendingApplications && team.pendingApplications.filter(app => app.status !== 'rejected').length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Pending Applications ({team.pendingApplications.filter(app => app.status !== 'rejected').length})</h4>
                  <div className="space-y-4">
                    {team.pendingApplications.filter(app => app.status !== 'rejected').map((application) => (
                      <div key={application.user._id} className="p-4 border border-gray-200 rounded-md bg-gray-50">
                        {/* Applicant Basic Info */}
                        <div className="mb-3">
                          <div className="mb-2">
                            <h5 className="font-semibold text-lg">
                              {application.user.firstName} {application.user.lastName}
                            </h5>
                          </div>
                          
                          {/* Contact Information and Action Buttons */}
                          <div className="flex justify-between items-start">
                            <div className="space-y-1 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-600">📧</span>
                                <span>{application.user.email}</span>
                              </div>
                              {application.user.phone && (
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-600">📞</span>
                                  <span>{application.user.phone}</span>
                                </div>
                              )}
                              {application.user.address && (
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-600">📍</span>
                                  <span>{getCityFromAddress(application.user.address)}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Action Buttons - Stacked Vertically */}
                            <div className="flex flex-col gap-2">
                              <Button
                                onClick={() => handleAcceptApplication(application.user._id)}
                                size="sm"
                                disabled={processingApplication === application.user._id}
                                className="px-2"
                              >
                                {processingApplication === application.user._id ? (
                                  <LoadingSpinner size="sm" />
                                ) : (
                                  <CheckCircle className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                onClick={() => {
                                  setSelectedApplication(application);
                                  setShowRejectDialog(true);
                                }}
                                variant="outline"
                                size="sm"
                                disabled={processingApplication === application.user._id}
                                className="px-2"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Skills */}
                        {getUserSkills(application.user).length > 0 && (
                          <div className="mb-3">
                            <h6 className="font-medium text-sm text-gray-700 mb-2">Skills:</h6>
                            <div className="flex flex-wrap gap-1">
                              {getUserSkills(application.user).map((skill, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {skill}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Application Notes */}
                        {application.notes && (
                          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <h6 className="font-medium text-sm text-blue-800 mb-1">Message:</h6>
                            <p className="text-sm text-blue-700">{application.notes}</p>
                          </div>
                        )}

                        {/* Application Date */}
                        <div className="text-xs text-gray-500">
                          Applied: {formatDate(application.appliedAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Join Code - Temporarily Disabled */}

        </div>
      </div>

      {/* Submission Dialog */}
      <Dialog open={showSubmissionDialog} onOpenChange={setShowSubmissionDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditingSubmission ? 'Edit Project Submission' : 'Submit Project'}</DialogTitle>
            <DialogDescription>
              {isEditingSubmission 
                ? 'Update your project submission for the hackathon. You can modify your presentation, GitHub repository details, and additional documents.'
                : 'Submit your final project for the hackathon. Upload your presentation, provide GitHub repository details, and add any additional documents with remarks.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Project Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Project Information</h3>
              
              <div>
                <label className="block text-sm font-medium mb-1">Project Title</label>
                <Input
                  value={submissionData.title}
                  readOnly
                  className="bg-gray-50"
                  placeholder="Project title will be set from assigned problem statement"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Title is automatically set from your assigned problem statement
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={submissionData.description}
                  readOnly
                  className="bg-gray-50"
                  placeholder="Project description will be set from assigned problem statement"
                  rows={4}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Description is automatically set from your assigned problem statement
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">General Remarks</label>
                <Textarea
                  value={submissionData.generalRemarks}
                  onChange={(e) => setSubmissionData(prev => ({ ...prev, generalRemarks: e.target.value }))}
                  placeholder="Add any general remarks about your submission..."
                  rows={3}
                  maxLength={2000}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {submissionData.generalRemarks.length}/2000 characters
                </p>
              </div>
            </div>

            {/* Presentation Upload */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Presentation</h3>
              
              <SubmissionFileUpload
                label="Presentation File"
                accept=".ppt,.pptx,.pdf"
                maxSize={50}
                onFileSelect={(file, fileKey) => {
                  setSubmissionData(prev => ({
                    ...prev,
                    presentation: { ...prev.presentation, file: fileKey }
                  }));
                }}
                onFileRemove={() => {
                  setSubmissionData(prev => ({
                    ...prev,
                    presentation: { ...prev.presentation, file: '' }
                  }));
                }}
                currentFile={submissionData.presentation.file ? {
                  name: submissionData.presentation.file.split('/').pop() || 'Presentation uploaded',
                  fileKey: submissionData.presentation.file
                } : null}
                remarks={submissionData.presentation.remarks}
                onRemarksChange={(remarks) => {
                  setSubmissionData(prev => ({
                    ...prev,
                    presentation: { ...prev.presentation, remarks }
                  }));
                }}
                uploadFunction={uploadAPI.uploadSubmissionPresentation}
                description="Upload your presentation file (PPT, PPTX, or PDF) - Max 50MB"
              />

              <div>
                <label className="block text-sm font-medium mb-1">Presentation Link (Alternative)</label>
                <Input
                  value={submissionData.presentationLink}
                  onChange={(e) => setSubmissionData(prev => ({ ...prev, presentationLink: e.target.value }))}
                  placeholder="https://slides.com/presentation (if not uploading file)"
                />
              </div>
            </div>

            {/* GitHub Repository */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">GitHub Repository</h3>
              
              <div>
                <label className="block text-sm font-medium mb-1">Repository Link *</label>
                <Input
                  value={submissionData.githubRepository.link}
                  onChange={(e) => setSubmissionData(prev => ({
                    ...prev,
                    githubRepository: { ...prev.githubRepository, link: e.target.value }
                  }))}
                  placeholder="https://github.com/username/repo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Repository Remarks</label>
                <Textarea
                  value={submissionData.githubRepository.remarks}
                  onChange={(e) => setSubmissionData(prev => ({
                    ...prev,
                    githubRepository: { ...prev.githubRepository, remarks: e.target.value }
                  }))}
                  placeholder="Add remarks about your repository, setup instructions, etc..."
                  rows={3}
                  maxLength={1000}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {submissionData.githubRepository.remarks.length}/1000 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Demo Link</label>
                <Input
                  value={submissionData.demoLink}
                  onChange={(e) => setSubmissionData(prev => ({ ...prev, demoLink: e.target.value }))}
                  placeholder="https://your-demo-link.com"
                />
              </div>
            </div>

            {/* Additional Documents */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Additional Documents</h3>
              
              {submissionData.additionalDocuments.map((doc, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">Document {index + 1}</h4>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSubmissionData(prev => ({
                          ...prev,
                          additionalDocuments: prev.additionalDocuments.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <SubmissionFileUpload
                    label="Document File"
                    accept=".pdf,.doc,.docx,.zip,.txt,.xls,.xlsx"
                    maxSize={100}
                    onFileSelect={(file, fileKey) => {
                      setSubmissionData(prev => ({
                        ...prev,
                        additionalDocuments: prev.additionalDocuments.map((d, i) => 
                          i === index ? { ...d, file: fileKey, name: file.name } : d
                        )
                      }));
                    }}
                    onFileRemove={() => {
                      setSubmissionData(prev => ({
                        ...prev,
                        additionalDocuments: prev.additionalDocuments.map((d, i) => 
                          i === index ? { ...d, file: '' } : d
                        )
                      }));
                    }}
                    currentFile={doc.file ? {
                      name: doc.name,
                      fileKey: doc.file
                    } : null}
                    remarks={doc.remarks}
                    onRemarksChange={(remarks) => {
                      setSubmissionData(prev => ({
                        ...prev,
                        additionalDocuments: prev.additionalDocuments.map((d, i) => 
                          i === index ? { ...d, remarks } : d
                        )
                      }));
                    }}
                    uploadFunction={uploadAPI.uploadSubmissionDocument}
                    description="Upload additional documents (PDF, Word, Excel, ZIP, TXT) - Max 100MB"
                  />
                </div>
              ))}
              
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSubmissionData(prev => ({
                    ...prev,
                    additionalDocuments: [...prev.additionalDocuments, { name: '', file: '', remarks: '' }]
                  }));
                }}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Document
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmissionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitProject}>
              {isEditingSubmission ? 'Update Submission' : 'Submit Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog open={showRemoveMemberDialog} onOpenChange={setShowRemoveMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this member from your team? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemoveMemberDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemoveMember}>
              Remove Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toggle Team Status Dialog */}
      <Dialog open={showToggleStatusDialog} onOpenChange={setShowToggleStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{team?.isActive ? 'Deactivate' : 'Activate'} Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to {team?.isActive ? 'deactivate' : 'activate'} this team? 
              {team?.isActive 
                ? ' This will make the team inactive but not delete it.'
                : ' This will make the team active again.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowToggleStatusDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmToggleTeamStatus}
              className={team?.isActive 
                ? "bg-red-600 hover:bg-red-700 text-white" 
                : "bg-green-600 hover:bg-green-700 text-white"
              }
            >
              {team?.isActive ? 'Deactivate' : 'Activate'} Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Team Dialog */}
      <Dialog open={showLeaveTeamDialog} onOpenChange={setShowLeaveTeamDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to leave this team? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveTeamDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmLeaveTeam}>
              Leave Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Application Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Application</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject this application? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Rejection Reason *</label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter rejection reason"
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRejectApplication}>
              Reject Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Notes</DialogTitle>
            <DialogDescription>
              Add or update team notes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Team Notes</label>
              <Textarea
                value={teamNotes}
                onChange={(e) => setTeamNotes(e.target.value)}
                placeholder="Enter team notes"
                rows={6}
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={handleUpdateTeamNotes} disabled={updatingNotes}>
                {updatingNotes ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  "Save Notes"
                )}
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    );
  } catch (error) {
    console.error('Error rendering TeamDetail:', error);
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="text-center py-8">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error Loading Team Details</h1>
          <p className="text-gray-600 mb-4">There was an error loading the team details. Please try refreshing the page.</p>
          <Button onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }
};

export default TeamDetail; 